import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import api, { billingAPI } from '../../services/api';
import { downloadBlobResponse, readBlobError } from '../../utils/download';

// Only ever hand the browser off to a known Stripe-hosted checkout host over
// HTTPS. A tampered/spoofed `checkout_url` from the API must never be able to
// turn this into an open-redirect (phishing) vector.
const ALLOWED_CHECKOUT_HOSTS = new Set(['checkout.stripe.com']);

function isAllowedCheckoutUrl(raw) {
  try {
    const u = new URL(raw);
    return u.protocol === 'https:' && ALLOWED_CHECKOUT_HOSTS.has(u.hostname);
  } catch {
    return false;
  }
}

export default function InvoiceDetail() {
  const { billing_id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const paymentResult = searchParams.get('payment'); // 'success' | 'cancelled'
  const [bill, setBill] = useState(null);
  const [loading, setLoading] = useState(true);
  const [payModal, setPayModal] = useState(false);
  const [payMethod, setPayMethod] = useState('MPESA');
  const [phone, setPhone] = useState('');
  const [paying, setPaying] = useState(false);
  const [mpesaStatus, setMpesaStatus] = useState(null);
  const [checkoutId, setCheckoutId] = useState(null);
  const [plans, setPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState('');
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [toast, setToast] = useState(null); // { kind: 'error'|'success', text }

  const showToast = (kind, text) => {
    setToast({ kind, text });
    setTimeout(() => setToast(null), 5000);
  };

  useEffect(() => {
    const fetchData = async () => {
      // Fetch bill and plans independently — plans may 403 for some roles
      try {
        const bRes = await api.get(`/billing/${billing_id}`);
        setBill(bRes.data.data);
      } catch { } finally { setLoading(false); }
      api.get('/billing/insurance/plans')
        .then(r => setPlans(r.data.data || []))
        .catch(() => {});
    };
    fetchData();
  }, [billing_id]);

  // Refetch bill status when Stripe redirects back with ?payment=success
  useEffect(() => {
    if (paymentResult === 'success') {
      api.get(`/billing/${billing_id}`)
        .then(r => setBill(r.data.data))
        .catch(() => {});
    }
  }, [paymentResult, billing_id]);

  useEffect(() => {
    if (!checkoutId) return;
    const interval = setInterval(async () => {
      try {
        const res = await api.get(`/billing/payments/mpesa/${checkoutId}/status`);
        const s = res.data.data;
        setMpesaStatus(s);
        if (s.status === 'COMPLETED') {
          clearInterval(interval);
          const updated = await api.get(`/billing/${billing_id}`);
          setBill(updated.data.data);
        } else if (s.status === 'FAILED' || s.status === 'CANCELLED') {
          clearInterval(interval);
        }
      } catch { }
    }, 3000);
    return () => clearInterval(interval);
  }, [checkoutId, billing_id]);

  const initiateMpesa = async () => {
    if (!phone) { alert('Enter phone number'); return; }
    setPaying(true);
    try {
      const res = await api.post('/billing/payments/mpesa/stk-push', {
        billing_id,
        phone_number: phone,
        amount: bill.patient_copay,
        account_reference: bill.invoice_number,
        transaction_desc: 'Home Health Service',
      });
      const d = res.data.data;
      setCheckoutId(d.CheckoutRequestID);
      setMpesaStatus({ status: 'PENDING' });
      if (d.demo) {
        alert('Demo mode: M-Pesa not configured. STK push simulated.');
      }
    } catch (e) {
      alert('M-Pesa initiation failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setPaying(false);
    }
  };

  const initiateStripe = async () => {
    setPaying(true);
    try {
      const res = await api.post('/billing/payments/stripe/create-checkout', {
        billing_id,
        amount_kes: bill.patient_copay,
        invoice_number: bill.invoice_number,
      });
      const d = res.data.data;
      if (d.demo) {
        alert('Demo mode: Stripe not configured. Add stripe_secret_key in Billing Config.');
        setPayModal(false);
      } else if (isAllowedCheckoutUrl(d.checkout_url)) {
        // Redirect to Stripe hosted checkout page (validated host + https).
        window.location.href = d.checkout_url;
      } else {
        alert('Stripe error: received an invalid checkout URL. Payment was not started.');
      }
    } catch (e) {
      alert('Stripe error: ' + (e.response?.data?.detail || e.message));
    } finally {
      setPaying(false);
    }
  };

  const submitInsuranceClaim = async () => {
    if (!selectedPlan) { alert('Select an insurance plan'); return; }
    setPaying(true);
    try {
      await api.post('/billing/insurance/claims', {
        billing_id,
        patient_id: bill.patient_id,
        plan_id: selectedPlan,
      });
      alert('Insurance claim submitted successfully!');
      setPayModal(false);
      const updated = await api.get(`/billing/${billing_id}`);
      setBill(updated.data.data);
    } catch (e) {
      alert('Claim submission failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setPaying(false);
    }
  };

  const downloadPdf = async () => {
    setDownloadingPdf(true);
    try {
      const res = await billingAPI.pdf(billing_id);
      downloadBlobResponse(res, `invoice-${bill?.invoice_number || billing_id}.pdf`);
      showToast('success', 'Invoice PDF downloaded.');
    } catch (e) {
      // 503 = PDF engine unavailable. Error bodies arrive as a Blob because we
      // requested responseType:'blob' — read it back for the server's detail.
      const status = e.response?.status;
      const body = e.response?.data instanceof Blob ? await readBlobError(e.response.data) : e.response?.data;
      if (status === 503) {
        showToast('error', body?.detail || 'PDF engine unavailable — please try again later.');
      } else {
        showToast('error', body?.detail || 'Could not generate the invoice PDF.');
      }
    } finally {
      setDownloadingPdf(false);
    }
  };

  const fmt = (n) => `KES ${Number(n || 0).toLocaleString()}`;

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#8b7681' }}>Loading invoice…</div>;
  if (!bill) return <div style={{ padding: 40, textAlign: 'center', color: '#c0392b' }}>Invoice not found.</div>;

  const isPaid = bill.payment_status === 'PAID';
  const breakdown = bill.cpt_codes?.[0] || {};

  return (
    <div style={{ fontFamily: 'Inter, sans-serif', width: '100%', boxSizing: 'border-box', maxWidth: 900, margin: '0 auto' }}>
      {/* Back */}
      <button onClick={() => navigate('/billing')}
        style={{ background: 'none', border: 'none', color: '#7b1e3c', cursor: 'pointer', fontSize: 13, fontWeight: 600, marginBottom: 16, padding: 0 }}>
        ← Back to Billing
      </button>

      {/* Toast (PDF download feedback) */}
      {toast && (
        <div style={{
          background: toast.kind === 'error' ? '#fdecea' : '#f0faf4',
          border: `1px solid ${toast.kind === 'error' ? '#ef9a9a' : '#27ae6033'}`,
          color: toast.kind === 'error' ? '#b71c1c' : '#27ae60',
          borderRadius: 10, padding: '12px 16px', marginBottom: 16, fontSize: 13, fontWeight: 600,
          display: 'flex', alignItems: 'center', gap: 10,
        }}>
          <span style={{ fontSize: 16 }}>{toast.kind === 'error' ? '⚠️' : '✅'}</span>
          <span style={{ flex: 1 }}>{toast.text}</span>
          <button onClick={() => setToast(null)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', fontSize: 16, lineHeight: 1, padding: 0 }}>×</button>
        </div>
      )}

      {/* Stripe redirect result banner */}
      {paymentResult === 'success' && (
        <div style={{ background: '#f0faf4', border: '1px solid #27ae6033', borderRadius: 10, padding: '14px 18px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: 22 }}>✅</span>
          <div>
            <div style={{ fontWeight: 800, color: '#27ae60', fontSize: 14 }}>Payment successful!</div>
            <div style={{ fontSize: 12, color: '#555', marginTop: 2 }}>Your card payment was processed by Stripe. Invoice will update shortly.</div>
          </div>
        </div>
      )}
      {paymentResult === 'cancelled' && (
        <div style={{ background: '#fef9f0', border: '1px solid #e67e2233', borderRadius: 10, padding: '14px 18px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: 22 }}>⚠️</span>
          <div>
            <div style={{ fontWeight: 800, color: '#e67e22', fontSize: 14 }}>Payment cancelled</div>
            <div style={{ fontSize: 12, color: '#555', marginTop: 2 }}>You cancelled the Stripe checkout. Try again when ready.</div>
          </div>
        </div>
      )}

      {/* Invoice header */}
      <div style={{ background: '#fff', border: '1px solid #ece3e7', borderRadius: 12, padding: 24, marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: '#2b1b22' }}>{bill.invoice_number}</h2>
            <p style={{ margin: '4px 0 0', color: '#8b7681', fontSize: 13 }}>
              {bill.auto_generated ? '⚡ Auto-generated on visit completion' : 'Manual invoice'}
            </p>
          </div>
          <div style={{ textAlign: 'right' }}>
            <span style={{
              background: isPaid ? '#f0faf4' : '#fef9f0',
              color: isPaid ? '#27ae60' : '#e67e22',
              border: `1px solid ${isPaid ? '#27ae6033' : '#e67e2233'}`,
              borderRadius: 999, padding: '4px 14px', fontSize: 13, fontWeight: 700,
            }}>{bill.payment_status}</span>
            <div style={{ fontSize: 12, color: '#8b7681', marginTop: 6 }}>Due: {bill.due_date || 'N/A'}</div>
            <button onClick={downloadPdf} disabled={downloadingPdf}
              style={{ marginTop: 10, padding: '8px 16px', borderRadius: 10, border: '1.5px solid #7b1e3c',
                background: '#fff', color: '#7b1e3c', fontWeight: 700, fontSize: 13,
                cursor: downloadingPdf ? 'not-allowed' : 'pointer', opacity: downloadingPdf ? 0.6 : 1 }}>
              {downloadingPdf ? 'Preparing…' : '⬇ Download PDF'}
            </button>
          </div>
        </div>
      </div>

      {/* Cost breakdown */}
      <div style={{ background: '#fff', border: '1px solid #ece3e7', borderRadius: 12, padding: 24, marginBottom: 16 }}>
        <h3 style={{ margin: '0 0 16px', fontSize: 14, fontWeight: 800, color: '#2b1b22' }}>Cost Breakdown</h3>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <tbody>
            {breakdown.service_type && (
              <tr style={{ borderBottom: '1px solid #f0ebed' }}>
                <td style={{ padding: '8px 0', color: '#555' }}>Service Type</td>
                <td style={{ padding: '8px 0', textAlign: 'right', fontWeight: 600 }}>{breakdown.service_type}</td>
              </tr>
            )}
            {breakdown.billable_minutes > 0 && (
              <tr style={{ borderBottom: '1px solid #f0ebed' }}>
                <td style={{ padding: '8px 0', color: '#555' }}>Duration ({breakdown.billable_minutes} min × KES {breakdown.billable_minutes > 0 ? (breakdown.duration_cost / breakdown.billable_minutes).toFixed(2) : 0}/min)</td>
                <td style={{ padding: '8px 0', textAlign: 'right', fontWeight: 600 }}>{fmt(breakdown.duration_cost)}</td>
              </tr>
            )}
            {breakdown.travel_km > 0 && (
              <tr style={{ borderBottom: '1px solid #f0ebed' }}>
                <td style={{ padding: '8px 0', color: '#555' }}>Travel ({breakdown.travel_km?.toFixed(1)} km)</td>
                <td style={{ padding: '8px 0', textAlign: 'right', fontWeight: 600 }}>{fmt(breakdown.travel_cost)}</td>
              </tr>
            )}
            <tr style={{ borderBottom: '1px solid #f0ebed' }}>
              <td style={{ padding: '8px 0', color: '#555' }}>Base Rate</td>
              <td style={{ padding: '8px 0', textAlign: 'right', fontWeight: 600 }}>{fmt(bill.base_rate_kes || breakdown.base_rate)}</td>
            </tr>
            <tr style={{ borderBottom: '1px solid #f0ebed' }}>
              <td style={{ padding: '8px 0', color: '#555' }}>Tax ({breakdown.tax_rate_pct || 16}%)</td>
              <td style={{ padding: '8px 0', textAlign: 'right' }}>{fmt(bill.tax_amount)}</td>
            </tr>
            {bill.insurance_covered > 0 && (
              <tr style={{ borderBottom: '1px solid #f0ebed' }}>
                <td style={{ padding: '8px 0', color: '#27ae60' }}>Insurance Covered</td>
                <td style={{ padding: '8px 0', textAlign: 'right', color: '#27ae60' }}>- {fmt(bill.insurance_covered)}</td>
              </tr>
            )}
            <tr>
              <td style={{ padding: '12px 0 0', fontWeight: 800, fontSize: 15, color: '#2b1b22' }}>Patient Copay</td>
              <td style={{ padding: '12px 0 0', textAlign: 'right', fontWeight: 800, fontSize: 16, color: '#7b1e3c' }}>{fmt(bill.patient_copay)}</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Payment section */}
      {!isPaid && (
        <div style={{ background: '#fff', border: '1px solid #ece3e7', borderRadius: 12, padding: 24 }}>
          <h3 style={{ margin: '0 0 16px', fontSize: 14, fontWeight: 800, color: '#2b1b22' }}>Record Payment</h3>
          <div style={{ display: 'flex', gap: 10 }}>
            <button onClick={() => { setPayMethod('MPESA'); setPayModal(true); }}
              style={{ flex: 1, padding: '12px 0', borderRadius: 10, border: '2px solid #27ae60', background: '#f0faf4', color: '#27ae60', fontWeight: 700, fontSize: 14, cursor: 'pointer' }}>
              📱 M-Pesa
            </button>
            <button onClick={() => { setPayMethod('CARD'); setPayModal(true); }}
              style={{ flex: 1, padding: '12px 0', borderRadius: 10, border: '2px solid #2980b9', background: '#eaf4fb', color: '#2980b9', fontWeight: 700, fontSize: 14, cursor: 'pointer' }}>
              💳 Card
            </button>
            <button onClick={() => { setPayMethod('INSURANCE'); setPayModal(true); }}
              style={{ flex: 1, padding: '12px 0', borderRadius: 10, border: '2px solid #8e44ad', background: '#f5eefb', color: '#8e44ad', fontWeight: 700, fontSize: 14, cursor: 'pointer' }}>
              🏥 Insurance
            </button>
          </div>
        </div>
      )}

      {/* Payment modal */}
      {payModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#fff', borderRadius: 16, padding: 32, width: 420, maxWidth: '90vw' }}>
            <h3 style={{ margin: '0 0 20px', color: '#2b1b22' }}>
              {payMethod === 'MPESA' ? '📱 M-Pesa Payment' : payMethod === 'CARD' ? '💳 Card Payment' : '🏥 Insurance Claim'}
            </h3>

            {payMethod === 'MPESA' && (
              <>
                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 700, marginBottom: 6, color: '#2b1b22' }}>Phone Number (07XXXXXXXX)</label>
                  <input value={phone} onChange={e => setPhone(e.target.value)} placeholder="0712345678"
                    style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #ddd', fontSize: 14, boxSizing: 'border-box' }} />
                </div>
                <div style={{ background: '#f0faf4', padding: 12, borderRadius: 8, marginBottom: 16, fontSize: 13, color: '#27ae60', fontWeight: 600 }}>
                  Amount: {fmt(bill.patient_copay)}
                </div>
                {mpesaStatus && (
                  <div style={{ padding: 12, borderRadius: 8, marginBottom: 16, background: mpesaStatus.status === 'COMPLETED' ? '#f0faf4' : '#fef9f0', fontSize: 13 }}>
                    Status: <strong>{mpesaStatus.status}</strong>
                    {mpesaStatus.receipt && <div>Receipt: {mpesaStatus.receipt}</div>}
                  </div>
                )}
                <div style={{ display: 'flex', gap: 10 }}>
                  <button onClick={initiateMpesa} disabled={paying}
                    style={{ flex: 1, padding: '10px 0', background: '#27ae60', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, cursor: paying ? 'not-allowed' : 'pointer', opacity: paying ? 0.6 : 1 }}>
                    {paying ? 'Sending…' : 'Send STK Push'}
                  </button>
                  <button onClick={() => setPayModal(false)}
                    style={{ padding: '10px 20px', background: '#f8f5f6', border: '1px solid #ddd', borderRadius: 8, cursor: 'pointer' }}>
                    Cancel
                  </button>
                </div>
              </>
            )}

            {payMethod === 'CARD' && (
              <>
                <div style={{ background: '#eaf4fb', padding: 14, borderRadius: 8, marginBottom: 16, fontSize: 13, color: '#2980b9' }}>
                  Amount to charge: {fmt(bill.patient_copay)}<br />
                  You will be redirected to Stripe's secure hosted checkout page.
                </div>
                <div style={{ display: 'flex', gap: 10 }}>
                  <button onClick={initiateStripe} disabled={paying}
                    style={{ flex: 1, padding: '10px 0', background: '#2980b9', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, cursor: paying ? 'not-allowed' : 'pointer', opacity: paying ? 0.6 : 1 }}>
                    {paying ? 'Redirecting…' : 'Pay with Card (Stripe)'}
                  </button>
                  <button onClick={() => setPayModal(false)}
                    style={{ padding: '10px 20px', background: '#f8f5f6', border: '1px solid #ddd', borderRadius: 8, cursor: 'pointer' }}>
                    Cancel
                  </button>
                </div>
              </>
            )}

            {payMethod === 'INSURANCE' && (
              <>
                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 700, marginBottom: 6, color: '#2b1b22' }}>Insurance Plan</label>
                  <select value={selectedPlan} onChange={e => setSelectedPlan(e.target.value)}
                    style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #ddd', fontSize: 14, boxSizing: 'border-box' }}>
                    <option value="">Select plan…</option>
                    {plans.filter(p => p.is_active).map(p => (
                      <option key={p.plan_id} value={p.plan_id}>{p.plan_name} {p.provider_name ? `(${p.provider_name})` : ''}</option>
                    ))}
                  </select>
                </div>
                <div style={{ display: 'flex', gap: 10 }}>
                  <button onClick={submitInsuranceClaim} disabled={paying}
                    style={{ flex: 1, padding: '10px 0', background: '#8e44ad', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, cursor: paying ? 'not-allowed' : 'pointer', opacity: paying ? 0.6 : 1 }}>
                    {paying ? 'Submitting…' : 'Submit Claim'}
                  </button>
                  <button onClick={() => setPayModal(false)}
                    style={{ padding: '10px 20px', background: '#f8f5f6', border: '1px solid #ddd', borderRadius: 8, cursor: 'pointer' }}>
                    Cancel
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
